import { useMemo, useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { useStable } from "../lib/useStable";
import { useViewer } from "../lib/viewer";
import { useBusyAction } from "../lib/useBusyAction";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Loading, Empty, ScreenBoundary } from "../components/states";
import {
  ActionBar,
  Button,
  Pill,
  StatusPill,
  UrgencyTag,
  Chips,
  CollapsibleSection,
  Sheet,
  plural,
  relativeDay,
  SectionHeading,
  SelectableRow,
  statusChips,
} from "../components/kit";
import { presentRequestOrigin, type StatusTone } from "@shared/status";
import type { Outcome } from "../lib/useBusyAction";
import { countOpen, isOpenLineStatus } from "@shared/openRequests";
import { useHeaderOffset } from "../lib/useHeaderOffset";

/**
 * Manager review.
 *
 * Gloria's manager marks *items*, so selection is per line and actions are
 * bulk. The Monday-morning case is thirty lines the manager agrees with, and
 * making that thirty separate confirmations is exactly the "too much manual
 * input" that got the previous tool rejected — so "Approve all" is one tap and
 * everything else is select-then-act.
 *
 * Three things a demo rehearsal caught, and where each is answered:
 *
 *  1. **"Ordered" and "From warehouse" had no stated difference**, and it is the
 *     hinge of the whole chain — one spends money with Henry Schein, the other
 *     ships stock Nexora already owns. The action bar now says it in one line
 *     above the buttons, and "What's the difference?" opens a sheet that spells
 *     out all five decisions. The button *labels* are unchanged on purpose:
 *     `tools/demo.mjs` clicks `^From warehouse$`, so renaming them silently
 *     breaks the harness.
 *  2. **The row did not show warehouse stock**, so "From warehouse" was a
 *     guess. Every row now carries the on-hand figure and says plainly whether
 *     it covers the quantity asked for. See `stockByItem` below for where that
 *     number comes from and why it is not on the request query.
 *  3. **Both actions produced "1 line updated"** — the mutation's return value
 *     read aloud. See `DECISIONS`.
 *
 * A flat wall of eleven-plus cards at 390px is also a rehearsal finding, so the
 * rows are grouped and foldable: by clinic for a cross-clinic role (the owner's
 * question is "which clinic is producing this"), by urgency for a clinic
 * manager (whose rows are all one clinic and whose question is "what cannot
 * wait"). Sections open by default — a manager who has to expand something
 * before she can work is back to twenty clicks.
 */

type Filter = "open" | "requested" | "approved" | "back-ordered" | "all";

type Decision =
  | "approved"
  | "ordered-vendor"
  | "fulfilled-warehouse"
  | "back-ordered"
  | "cancelled";

/**
 * What each decision *is*, and what the toast says once it is made.
 *
 * Five buttons used to produce one identical sentence — "1 line updated" for
 * approving a line and for pulling it off the warehouse shelf, which are not
 * remotely the same act. The rehearsal caught it twice: the confirmation
 * confirmed nothing, and the two most easily confused buttons were the two that
 * read identically afterwards.
 *
 * Every toast is now the notify.* standard and nothing else: the loud half is
 * what a person would say happened, the quiet half is what happens next.
 *
 * There used to be a third clause — "… N request lines updated." — carried
 * solely because `tools/demo.mjs` checkbox 2 waited on `/lines? updated/`. The
 * tools owner has relaxed that selector to
 * `/ship from the warehouse|will ship|lines? updated/`, so the jargon is gone.
 * One thing is still load-bearing for the harness and must not be reworded
 * casually: **the warehouse toast has to contain "will ship"**, which
 * `happened` below does naturally ("2 items will ship from the warehouse").
 */
const DECISIONS: Record<
  Decision,
  {
    /** The button, and the sheet's heading. */
    label: string;
    /** What it means, in the sheet. One sentence, no jargon. */
    meaning: string;
    /** The toast's loud half. `n` is how many lines actually changed. */
    happened: (n: number) => string;
    /** The toast's quiet half: what happens next, as a whole sentence. */
    next: string;
  }
> = {
  approved: {
    label: "Approve",
    meaning:
      "You agree the clinic should have it. It stays open until someone orders it from the vendor or ships it from the warehouse.",
    happened: (n) => `${n} ${plural(n, "item")} approved`,
    next: "They stay on this screen until they are ordered or shipped.",
  },
  "ordered-vendor": {
    label: "Ordered",
    meaning:
      "You have bought it from the vendor — Henry Schein. Nexora's warehouse stock does not move, and the clinic waits for the vendor's delivery.",
    happened: (n) => `${n} ${plural(n, "item")} ordered from the vendor`,
    next: "They stay open until the vendor's delivery arrives.",
  },
  "fulfilled-warehouse": {
    label: "From warehouse",
    meaning:
      "You are shipping stock Nexora already holds. Nothing is bought, warehouse stock drops when it is dispatched, and the clinic is invoiced for what it receives.",
    happened: (n) => `${n} ${plural(n, "item")} will ship from the warehouse`,
    next: "They are on the warehouse pick list now.",
  },
  "back-ordered": {
    label: "Back order",
    meaning:
      "The vendor cannot supply it yet. It stays red — still needing ordering — so it cannot be forgotten.",
    happened: (n) => `${n} ${plural(n, "item")} marked back ordered`,
    next: "They stay red until the vendor can ship.",
  },
  cancelled: {
    label: "Cancel these",
    meaning: "The clinic is not getting it. The line closes and stops counting.",
    happened: (n) => `${n} ${plural(n, "item")} cancelled`,
    next: "The clinic sees them closed on its own request list.",
  },
};

/** What the row's stock figure says. Colour is the app's, not a new one: red is
 *  "somebody has to order this", which is exactly what an empty shelf means. */
function stockNote(available: number | undefined, qty: number) {
  if (available === undefined) return null;
  if (available === 0)
    return { text: "Nothing on the warehouse shelf — this must be ordered", bad: true };
  if (available < qty)
    return {
      text: `Warehouse has ${available} of the ${qty} asked for`,
      bad: true,
    };
  return { text: `Warehouse has ${available} — enough to cover this`, bad: false };
}

export function Approvals() {
  // Remounting the boundary is how a failed query gets retried: Convex throws
  // during render, so there is nothing to re-run without a fresh subtree.
  const [attempt, setAttempt] = useState(0);
  return (
    <ScreenBoundary
      key={attempt}
      title="Approvals could not load"
      heading={<SectionHeading title="Approvals" />}
      onRetry={() => setAttempt((n) => n + 1)}
    >
      <ApprovalsScreen />
    </ScreenBoundary>
  );
}

function ApprovalsScreen() {
  const viewer = useViewer();
  const counts = useQuery(api.requests.statusCounts, {});
  const [filter, setFilter] = useState<Filter>("open");

  /* `useStable`: same as Requests — the chip row drives these arguments, and
     without it every tap unmounts the screen into a skeleton mid-triage. */
  const lines = useStable(
    useQuery(api.requests.list, {
      openOnly: filter === "open",
      status: filter === "open" || filter === "all" ? undefined : filter,
    }),
  );

  /**
   * Warehouse availability per item — one shelf count per item, no money in it
   * at all, gated server-side with the same predicate `approvals.decide` uses.
   * An item with no warehouse row is absent rather than zero-padded; the `?? 0`
   * the readers below already apply is the same answer.
   */
  const availability = useQuery(api.inventory.availabilityByItem, {});

  const decide = useMutation(api.approvals.decide);
  const approveAll = useMutation(api.approvals.approveAllOpen);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { run, busy } = useBusyAction();
  const [helpOpen, setHelpOpen] = useState(false);
  const headerOffset = useHeaderOffset();

  type Row = NonNullable<typeof lines>[number];
  type Group = {
    key: string;
    title: string;
    tone: StatusTone;
    rows: Row[];
    /** Counted while the rows are being placed, not by walking each finished
     *  group again in the render loop. The header needs both figures on every
     *  render, and neither of them changes when a row is ticked. */
    urgent: number;
    shortOfStock: number;
  };

  /* Grouped by the question the reader is actually asking. A cross-clinic role
     asks "which clinic"; a clinic manager, whose rows are all one clinic, asks
     "what cannot wait". */
  const byClinic = viewer.clinicId == null;

  /** Rebuilt only when the shelf itself changes — not once per selection
   *  tick. */
  const stockByItem = useMemo(
    () =>
      new Map<string, number>(
        (availability ?? []).map((row) => [
          row.itemId as string,
          row.warehouseAvailable,
        ]),
      ),
    [availability],
  );

  /* A top-up is the system's own order and does not belong scattered through
     the urgency buckets: it is separated out here so the manual request lines
     group exactly as they did before, and the top-ups become their own cards
     below. `origin` is server-supplied per line — absent reads as `manual`. */
  const manualLines = useMemo(
    () => (lines ?? []).filter((line) => line.origin === "manual"),
    [lines],
  );
  const topUpLines = useMemo(
    () => (lines ?? []).filter((line) => line.origin === "top-up"),
    [lines],
  );

  /* Deliberately not keyed on `selected`: ticking a row changes which rows are
     highlighted, never which group they are in or what order they are in. A
     manager ticking thirty lines was regrouping and re-sorting the whole screen
     thirty times. */
  const groups = useMemo<Group[]>(() => {
    const rows = manualLines;
    const made: Group[] = [];
    const tally = (group: Group, line: Row) => {
      group.rows.push(line);
      if (line.urgency === "urgent") group.urgent += 1;
      const available = stockByItem.get(line.itemId as string);
      if (available !== undefined && available < line.qty) {
        group.shortOfStock += 1;
      }
    };

    if (byClinic) {
      const index = new Map<string, Group>();
      for (const line of rows) {
        let group = index.get(line.clinicName);
        if (!group) {
          group = {
            key: line.clinicName,
            title: line.clinicName,
            tone: "gray",
            rows: [],
            urgent: 0,
            shortOfStock: 0,
          };
          index.set(line.clinicName, group);
          made.push(group);
        }
        tally(group, line);
      }
      made.sort((a, b) => b.rows.length - a.rows.length);
    } else {
      /* The bucket owns its own predicate. Routine used to be special-cased
         inside the loop, which meant the table said what the three buckets were
         while the code beside it said something else for one of them. */
      const buckets: Array<{
        key: string;
        title: string;
        tone: StatusTone;
        match: (line: Row) => boolean;
      }> = [
        {
          key: "urgent",
          title: "Urgent",
          tone: "amber",
          match: (line) => line.urgency === "urgent",
        },
        {
          key: "soon",
          title: "Needed soon",
          tone: "amber",
          match: (line) => line.urgency === "soon",
        },
        {
          key: "routine",
          title: "Routine",
          tone: "gray",
          match: (line) =>
            line.urgency !== "urgent" && line.urgency !== "soon",
        },
      ];
      for (const bucket of buckets) {
        const group: Group = {
          key: bucket.key,
          title: bucket.title,
          tone: bucket.tone,
          rows: [],
          urgent: 0,
          shortOfStock: 0,
        };
        for (const line of rows.filter(bucket.match)) tally(group, line);
        if (group.rows.length > 0) made.push(group);
      }
    }

    // Oldest first inside a group: the request nobody has touched for three
    // weeks is the one that loses the client, not the one raised this morning.
    for (const group of made) group.rows.sort((a, b) => a.createdAt - b.createdAt);
    return made;
  }, [manualLines, byClinic, stockByItem]);

  /* A top-up is one `requests` row with several lines; it renders as one card
     per request. All the lines of a top-up share a createdAt and a requester
     (the counting assistant), so the header reads them off the first row. */
  type TopUpGroup = {
    requestId: string;
    clinicName: string;
    requestedByName: string;
    createdAt: number;
    rows: Row[];
  };

  const topUpGroups = useMemo<TopUpGroup[]>(() => {
    const index = new Map<string, TopUpGroup>();
    for (const line of topUpLines) {
      let group = index.get(line.requestId);
      if (!group) {
        group = {
          requestId: line.requestId,
          clinicName: line.clinicName,
          requestedByName: line.requestedByName,
          createdAt: line.createdAt,
          rows: [],
        };
        index.set(line.requestId, group);
      }
      group.rows.push(line);
    }
    const made = [...index.values()];
    // Stable item order within a card; newest top-up first between cards.
    for (const group of made)
      group.rows.sort((a, b) => a.itemName.localeCompare(b.itemName));
    made.sort((a, b) => b.createdAt - a.createdAt);
    return made;
  }, [topUpLines]);

  if (lines === undefined) {
    return (
      <div data-screen-state="loading">
        <Loading rows={5} label="Loading requests" />
      </div>
    );
  }

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  /* One decision applied to an explicit set of line ids. Both the bulk action
     bar and the top-up card's one-tap approve route through here, so a partial
     result is reported the same way from both. Selection is cleared of exactly
     the ids acted on — a top-up approve must not also drop a manual line the
     manager still had ticked. */
  const decideLines = (lineIds: string[], decision: Decision, failed: string) => {
    if (lineIds.length === 0) return;
    const spec = DECISIONS[decision];
    return run(
      async () => {
        const result = await decide({
          lineIds: lineIds as Id<"requestLines">[],
          decision,
        });
        // Success only, so it sits at the end of the action rather than after
        // the await in a caller that cannot tell the two paths apart.
        setSelected((current) => {
          const next = new Set(current);
          for (const id of lineIds) next.delete(id);
          return next;
        });
        return result;
      },
      {
        failed,
        /* Two sentences about one action, which is the case the array form
           exists for. Partial success is reported, not swallowed — a silent
           no-op is how a manager loses faith in the whole screen — and the
           warning deliberately carries none of the phrases checkbox 2 waits
           on, because a warning that satisfied the harness's wait would report
           a pass while telling the operator the opposite of what happened. */
        done: (result) =>
          ([
            result.changed > 0
              ? { happened: spec.happened(result.changed), next: spec.next }
              : null,
            result.reasons.length > 0
              ? {
                  happened: `${result.skipped} ${plural(result.skipped, "item")} left as ${
                    result.skipped === 1 ? "it was" : "they were"
                  }`,
                  next: result.reasons.join(" · "),
                  check: true,
                }
              : null,
          ] satisfies Array<Outcome | null>).filter(
            (outcome) => outcome !== null,
          ),
      },
    );
  };

  const act = (decision: Decision) =>
    decideLines([...selected], decision, "Could not update those items");

  /* The whole order in one tap. `approveAllOpen` deliberately refuses to sweep
     top-ups — approving the system's own order on the manager's behalf would
     make approval nominal — so approval stays a real decision and is made here,
     over exactly this request's still-open lines. */
  const approveTopUp = (rows: Row[]) =>
    decideLines(
      rows
        .filter((line) => isOpenLineStatus(line.lineStatus))
        .map((line) => line._id),
      "approved",
      "Could not approve the top-up",
    );

  const runApproveAll = () =>
    run(() => approveAll({}), {
      failed: "Could not approve those items",
      done: (result) =>
        result.changed === 0
          ? {
              happened: "Nothing waiting to approve",
              next: "Every request has already been dealt with.",
            }
          : {
              happened: DECISIONS.approved.happened(result.changed),
              next: DECISIONS.approved.next,
            },
    });

  // One definition, in shared/openRequests.ts. This used to add requested and
  // approved by hand and drop back-ordered, so the header said one number while
  // the Open chip and the rows under it — which go through the server's own
  // `openOnly` — said another.
  const openCount = countOpen(counts);

  /* What "Approve all" will actually sweep. `approveAllOpen` moves manual
     `requested` lines to `approved` and leaves top-ups alone, so the label
     must count exactly those — `counts.requested` includes the top-up lines
     the sweep will not touch, which is how "Approve all 49" approved 45.
     Derived from the loaded lines, which carry every line's origin, so the
     number on the button and the number the mutation delivers are the same. */
  const manualRequested = lines.filter(
    (line) => line.origin === "manual" && line.lineStatus === "requested",
  ).length;

  /** How much of the selection the warehouse could actually cover. Answers the
   *  "From warehouse" question for the whole selection, not one row at a time.
   *  One walk of the lines, because the count is the only thing anyone reads. */
  let coverable = 0;
  for (const line of lines) {
    if (!selected.has(line._id)) continue;
    const available = stockByItem.get(line.itemId as string);
    if (available !== undefined && available >= line.qty) coverable += 1;
  }

  return (
    <div
      data-screen-state="ready"
      /* Bottom clearance is the shell's, driven by the <ActionBar>'s own
         measured height — see `.app-main` in index.css. */
      className="space-y-4"
    >
      <SectionHeading
        title="Approvals"
        subtitle={`${viewer.clinicName ?? "All clinics"} · ${openCount} waiting`}
        action={
          manualRequested > 0 ? (
            <Button onClick={runApproveAll} disabled={busy} className="shrink-0">
              Approve all {manualRequested}
            </Button>
          ) : null
        }
      />

      <Chips<Filter>
        value={filter}
        onChange={(value) => {
          setFilter(value);
          setSelected(new Set());
        }}
        options={[
          { value: "open", label: "Open", count: openCount },
          // Which statuses a manager is offered is this screen's call; how each
          // one is worded is not.
          ...statusChips(["requested", "approved", "back-ordered"], counts),
          { value: "all", label: "All" },
        ]}
      />

      {lines.length === 0 ? (
        <Empty
          tone="good"
          title={filter === "open" ? "Nothing waiting" : "Nothing here"}
          body={
            filter === "open"
              ? "Every request has been dealt with."
              : "No lines with that status right now."
          }
        />
      ) : (
        <div className="space-y-4">
          {/* The system's own orders, one card per top-up request, above the
              manual buckets and never mixed into them. The amber badge is a
              qualifier saying where the order came from — the row's own status
              pill still says where each line stands. */}
          {topUpGroups.length > 0 ? (
            <div className="space-y-3">
              {topUpGroups.map((group) => {
                const badge = presentRequestOrigin(group.rows[0].origin);
                const openRows = group.rows.filter((line) =>
                  isOpenLineStatus(line.lineStatus),
                );
                return (
                  <Card
                    key={group.requestId}
                    className="gap-0 border-l-4 border-l-amber-400 py-0"
                  >
                    <div className="space-y-3 p-3">
                      <div className="min-w-0 space-y-1.5">
                        {badge ? (
                          <Pill
                            tone={badge.tone}
                            label={badge.label}
                            qualifier={badge.qualifier}
                          />
                        ) : null}
                        <p className="truncate text-xs text-muted-foreground">
                          {byClinic ? `${group.clinicName} · ` : ""}
                          {group.rows.length} {plural(group.rows.length, "item")} ·
                          counted by {group.requestedByName} ·{" "}
                          {relativeDay(group.createdAt)}
                        </p>
                      </div>
                      {/* One tap approves the whole order. `size="lg"` — this is
                          the headline decision on a screen a manager works on a
                          phone. Hidden once nothing on the order is still open. */}
                      {openRows.length > 0 ? (
                        <Button
                          size="lg"
                          onClick={() => approveTopUp(group.rows)}
                          disabled={busy}
                          className="w-full"
                        >
                          Approve top-up
                        </Button>
                      ) : null}
                    </div>
                    {/* Each line still selects individually, so a manager can
                        reject or reroute one line before taking the rest. */}
                    <div className="divide-y border-t">
                      {group.rows.map((line) => (
                        <SelectableRow
                          key={line._id}
                          selected={selected.has(line._id)}
                          onToggle={() => toggle(line._id)}
                        >
                          <>
                            <span className="flex items-baseline gap-2">
                              <span className="num text-base font-bold">
                                ×{line.qty}
                              </span>
                              <span className="min-w-0 flex-1 truncate text-sm font-semibold">
                                {line.itemName}
                              </span>
                            </span>
                            <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                              <StatusPill status={line.lineStatus} />
                            </span>
                            {/* In-flight is not netted out of a top-up: the item
                                is already coming, so the manager is told and can
                                trim the line rather than the system guessing. */}
                            {line.alreadyOnOrder ? (
                              <span className="mt-1.5 block text-xs text-muted-foreground">
                                Already on an open order — trim before approving
                                if it isn't needed
                              </span>
                            ) : null}
                          </>
                        </SelectableRow>
                      ))}
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : null}

          {groups.length > 0 ? (
            <div className="space-y-3 lg:grid lg:grid-cols-2 lg:items-start lg:gap-3 lg:space-y-0">
              {groups.map((group) => {
            const { urgent, shortOfStock } = group;
            const oldest = group.rows[0]?.createdAt;

            return (
              <CollapsibleSection
                key={group.key}
                title={group.title}
                count={group.rows.length}
                tone={urgent > 0 ? "amber" : group.tone}
                stickyTop={headerOffset}
                summary={[
                  byClinic && urgent > 0 ? `${urgent} urgent` : null,
                  oldest ? `oldest ${relativeDay(oldest)}` : null,
                  shortOfStock > 0
                    ? `${shortOfStock} not covered by the warehouse`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              >
                <div className="divide-y">
                  {group.rows.map((line) => {
                    const isSelected = selected.has(line._id);
                    const note = stockNote(
                      stockByItem.get(line.itemId as string),
                      line.qty,
                    );
                    return (
                      <SelectableRow
                        key={line._id}
                        selected={isSelected}
                        onToggle={() => toggle(line._id)}
                      >
                        <>
                          {/* What, and how many. The only line at full size. */}
                          <span className="flex items-baseline gap-2">
                            <span className="num text-base font-bold">
                              ×{line.qty}
                            </span>
                            <span className="min-w-0 flex-1 truncate text-sm font-semibold">
                              {line.itemName}
                            </span>
                          </span>
                          {/* Where it stands. Urgency goes through UrgencyTag —
                              it was rendering the raw lowercase database string
                              in a generic amber pill. */}
                          <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            <StatusPill status={line.lineStatus} />
                            <UrgencyTag urgency={line.urgency} />
                          </span>
                          {/* Can we ship it ourselves? The whole reason
                              "From warehouse" was a guess. */}
                          {note ? (
                            <span
                              className={cn(
                                "mt-1.5 block text-xs font-medium",
                                note.bad
                                  ? "text-red-700"
                                  : "text-muted-foreground",
                              )}
                            >
                              {note.text}
                            </span>
                          ) : null}
                          {/* Who asked, and when. */}
                          <span className="mt-1.5 block truncate text-xs text-muted-foreground">
                            {byClinic ? `${line.clinicName} · ` : ""}
                            {line.requestedByName} · {relativeDay(line.createdAt)}
                          </span>
                          {/* Anything they wrote. Quiet, and set apart from the
                              row's own facts by a rule rather than by colour. */}
                          {line.requestNotes ? (
                            <span className="mt-1.5 block border-l-2 pl-2 text-xs text-muted-foreground italic">
                              “{line.requestNotes}”
                            </span>
                          ) : null}
                          {/* Real chatter off the client's own order sheet. */}
                          {line.vendorPromoNote ? (
                            <span className="mt-1.5 block border-l-2 bg-muted py-1 pr-2 pl-2 text-xs text-muted-foreground">
                              {line.vendorPromoNote}
                            </span>
                          ) : null}
                        </>
                      </SelectableRow>
                    );
                  })}
                </div>
              </CollapsibleSection>
            );
              })}
            </div>
          ) : null}
        </div>
      )}

      {selected.size > 0 ? (
        <ActionBar className="space-y-2">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="min-w-0">
              <span className="font-semibold">{selected.size} selected</span>
              {availability === undefined ? null : (
                <span className="text-muted-foreground">
                  {" "}
                  · warehouse covers {coverable} of {selected.size}
                </span>
              )}
            </span>
            {/* The kit's `ghost` at the registry's `sm`. It used to compose on
                the registry base with `variant`/`size` at null and then undo
                the focus ring; there is nothing left to undo. */}
            <Button
              variant="ghost"
              onClick={() => setSelected(new Set())}
              className="-my-2 -mr-2 shrink-0"
            >
              Clear
            </Button>
          </div>

          {/* The one line the rehearsal asked for, above the two buttons it is
              about. The sheet has the long version; this is what stops a wrong
              tap without anyone opening anything. */}
          <p className="text-xs leading-snug text-muted-foreground">
            <b className="font-semibold text-foreground">Ordered</b> buys it from
            the vendor.{" "}
            <b className="font-semibold text-foreground">From warehouse</b> ships
            stock Nexora already holds.{" "}
            {/* Deliberately raw: an inline trigger inside a sentence. The
                registry base would make it `inline-flex whitespace-nowrap
                text-sm`, so three words that currently wrap with the paragraph
                would become an unwrappable box at its own size, on a 390px
                screen, inside `text-xs` copy. */}
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80"
            >
              What's the difference?
            </button>
          </p>

          {/* Approve is the decision this screen exists for: full width, first
              in the row on a desktop. The three exceptions are secondary, and
              cancelling — the rarest and the only destructive one — is quieter
              still. Five actions, three rows on a phone, one row on a desktop.

              All five carry `size="lg"`: these are *the* decision actions on a
              screen a manager works through on a phone, which is the whole of
              what the large size is reserved for. Nothing else on this screen
              takes it — "Approve all" in the heading is stock.

              The labels are load-bearing for `tools/demo.mjs`, which clicks
              `^From warehouse$`. Change them and the harness goes red. */}
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
            <Button
              size="lg"
              onClick={() => act("approved")}
              disabled={busy}
              className="col-span-2 lg:col-span-1"
            >
              Approve
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => act("ordered-vendor")}
              disabled={busy}
            >
              Ordered
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => act("fulfilled-warehouse")}
              disabled={busy}
            >
              From warehouse
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => act("back-ordered")}
              disabled={busy}
            >
              Back order
            </Button>
            <Button
              variant="ghost"
              size="lg"
              onClick={() => act("cancelled")}
              disabled={busy}
            >
              Cancel these
            </Button>
          </div>
        </ActionBar>
      ) : null}

      <Sheet
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        title="What each decision does"
        description="The two that look alike are the first two."
        footer={
          <Button size="lg" className="w-full" onClick={() => setHelpOpen(false)}>
            Got it
          </Button>
        }
      >
        <dl className="space-y-3 pb-2">
          {(Object.keys(DECISIONS) as Decision[]).map((key) => (
            /* The registry card, laid out by this call site: `block flex-row
               gap-0` undoes the base's `flex flex-col gap-6` for a box whose
               children are a <dt> and a <dd>, and `p-3` its `py-6`. Grey rather
               than white on purpose: these sit on the sheet's own white, so a
               white card would have only a hairline to distinguish it. */
            <Card key={key} className="block flex-row gap-0 bg-muted p-3">
              <dt className="text-sm font-bold">{DECISIONS[key].label}</dt>
              <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">
                {DECISIONS[key].meaning}
              </dd>
            </Card>
          ))}
        </dl>
      </Sheet>
    </div>
  );
}
